import { useSelector } from "react-redux";
import { CurrentUserByType, UserPermission } from "models/User";
import { RootState } from "store";
import { selectFactoringProfile } from "store/user/selector";

export function useUserFeature() {
  const {
    userDetails,
    account,
    userPermission,
    invitedGroupLength
  }: {
    userDetails: any;
    account: CurrentUserByType;
    userPermission: UserPermission;
    invitedGroupLength?: number;
  } = useSelector((state: RootState) => state.authState);
  const factoringProfile = useSelector(selectFactoringProfile);

  const isFactoringApproved = userDetails?.factoring_approved;
  const isAdmin = userDetails?.is_admin;
  const isBulkImportAllowed = factoringProfile?.allow_bulk_import;
  const isBroker =
    account?.type === "broker" ||
    account?.type === "dispatcher" ||
    account?.type === "broker/carrier";
  const isShipper = account?.type === "shipper";
  const isCarrier = account?.type === "carrier";
  const isInvitedCarrier = userPermission?.user_type.find((type) => type === "Invited Carrier")
    ? true
    : false;
  const isCarrierTrial = account?.type === "carrier" && !userDetails?.permissions?.is_subscribed;
  const isCarrierSubscribed =
    account?.type === "carrier" && userDetails?.permissions?.is_subscribed;
  const isBrokerTrial = account?.type === "broker" && !userDetails?.permissions?.is_subscribed;
  const isBrokerSubscribed = account?.type === "broker" && userDetails?.permissions?.is_subscribed;
  const canAccessReceivables =
    Boolean(isBrokerSubscribed) &&
    Boolean(userPermission?.available_contract_types?.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM);
  const isDispatcher = account?.type === "dispatcher";
  const isSubscribed = userDetails?.permissions?.is_subscribed;
  const isAllowLineItems = factoringProfile?.allow_line_itemized_items;

  const isSavedPostsRole = !isBroker && !isDispatcher;
  const isSavedLoadsRole = !isCarrier && !isInvitedCarrier;
  const isARAPRole = !isCarrierSubscribed && !isBrokerTrial && !isCarrierTrial;
  const isTextNotificationRole = !isShipper && !isDispatcher;
  const isSubscribedUser = isCarrierSubscribed || isBrokerSubscribed;
  const isRequestPayRole = isCarrierSubscribed;
  const isBulkUploadRole = isBulkImportAllowed && (isBrokerSubscribed || isCarrierSubscribed);
  const isHistoryRole = isCarrierSubscribed;
  const isConnectionsRole = isInvitedCarrier && Boolean(invitedGroupLength);
  const isAssignedLoadsRole = isCarrier && isInvitedCarrier;
  const isFactoringRole = !(isBrokerSubscribed || isShipper || isDispatcher || isCarrierSubscribed);
  const isPostLoadRole = isShipper || isBroker || isDispatcher;
  const isTruckSearchRole = isBroker || isDispatcher;
  const isHelpRole = !isDispatcher && !isShipper;
  const isFactoringType =
    isInvitedCarrier || isCarrierTrial || isCarrierSubscribed || isBrokerTrial;
  const isHelpType = isCarrierTrial || isCarrierSubscribed || isBrokerSubscribed;
  const isWalletEnabled = isCarrier
    ? userDetails?.stripe_enabled ?? false
    : userDetails?.allow_self_finance_fr ?? false;
  const isNonFactorRefactorEnabled = factoringProfile?.use_new_non_factored_funding_requests;
  const isAutoGenerateLoadNumbersEnabled = factoringProfile?.auto_generate_load_numbers;
  const exposeDebtorCreditLimit = factoringProfile?.expose_debtor_credit_limit;
  const showCarrierAdditionalDiscountRate =
    factoringProfile?.company_settings?.show_carrier_additional_discount_rate;
  const showAdditionalDiscountRate = isBroker || showCarrierAdditionalDiscountRate;
  const showExternalCode = isCarrier || Boolean(factoringProfile?.datasync_option);
  const isAlvysEnabled = isBroker && Boolean(factoringProfile?.alvys_enabled);
  return {
    isAdmin,
    isAllowLineItems,
    isFactoringApproved,
    isBulkImportAllowed,
    isBroker,
    isShipper,
    isCarrier,
    isInvitedCarrier,
    isCarrierTrial,
    isCarrierSubscribed,
    isBrokerTrial,
    isBrokerSubscribed,
    canAccessReceivables,
    isDispatcher,
    isSavedPostsRole,
    isSavedLoadsRole,
    isARAPRole,
    isTextNotificationRole,
    isAssignedLoadsRole,
    isConnectionsRole,
    isBulkUploadRole,
    isFactoringRole,
    isSubscribedUser,
    isHelpRole,
    isHistoryRole,
    isPostLoadRole,
    isRequestPayRole,
    isTruckSearchRole,
    isFactoringType,
    isHelpType,
    isSubscribed,
    isWalletEnabled,
    isNonFactorRefactorEnabled,
    isAutoGenerateLoadNumbersEnabled,
    exposeDebtorCreditLimit,
    showAdditionalDiscountRate,
    showExternalCode,
    isAlvysEnabled
  };
}
